// Unico step obrigatorio do fluxo (cria workspace + usuario administrador).
import { formatCnpj, formatCpf, fmtTelefone, isCpfLen } from "@/utils/fmt";
import { Icon } from "@/components/common/Icon";
import { Checkbox, Field } from "@/components/onboarding/OnbFields";
import { useCnpjLookup } from "@/hooks/useCnpjLookup";

import { WsVerifyField } from "./WsVerifyField";
import type { WsForm, WsSetField } from "./types";

interface Props {
  form: WsForm;
  setField: WsSetField;
}

const UFS = [
  "SP", "RJ", "MG", "RS", "SC", "PR", "BA", "CE",
  "GO", "AM", "PE", "DF", "ES", "PA", "MA",
];

export function WsStepWorkspace({ form, setField }: Props) {
  const cnpjLookup = useCnpjLookup();
  const looking = cnpjLookup.isPending;
  const isPj = form.docType === "cnpj";

  const lookup = async () => {
    if (!isPj) return;
    if (!form.cnpj) return;
    try {
      const result = await cnpjLookup.mutateAsync(form.cnpj);
      // Sobrescreve o que veio da Receita; user pode editar depois.
      if (result.razao_social) setField("razao", result.razao_social);
      if (result.nome_fantasia) setField("fantasia", result.nome_fantasia);
      // Endereço estruturado (logradouro vai em `endereco`).
      if (result.logradouro) setField("endereco", result.logradouro);
      else if (result.address) setField("endereco", result.address);
      if (result.numero) setField("numero", result.numero);
      if (result.complemento) setField("complemento", result.complemento);
      if (result.bairro) setField("bairro", result.bairro);
      if (result.cep) setField("cep", result.cep);
      if (result.cidade) setField("cidade", result.cidade);
      if (result.uf) setField("uf", result.uf);
      if (result.phone) setField("telefone", fmtTelefone(result.phone));
      if (result.email) setField("emailEmpresa", result.email);
    } catch (err) {
      console.warn("Lookup CNPJ falhou:", err);
    }
  };

  const setDocType = (docType: "cnpj" | "cpf") => {
    setField("docType", docType);
    if (docType === "cnpj") {
      setField("cpf", "");
      setField("nomePf", "");
    } else {
      setField("cnpj", "");
      setField("razao", "");
      setField("fantasia", "");
      setField("respNome", "");
      setField("respCpf", "");
      setField("respCargo", "");
    }
  };

  return (
    <>
      <div className="onb-section-head">
        <div className="onb-step-eyebrow">ETAPA 1 · OBRIGATÓRIA</div>
        <h1 className="onb-step-title">Crie seu workspace</h1>
        <p className="onb-step-sub">
          Identifique sua empresa (CNPJ) ou você como profissional (CPF) e o
          usuário administrador. Depois disso seu workspace já existe e você
          pode continuar pelos próximos passos ou voltar mais tarde.
        </p>
      </div>

      <div className="onb-section">
        <div className="onb-section-title">Tipo de cadastro</div>
        <p className="onb-section-sub">
          Workspaces podem representar uma empresa (PJ) ou uma pessoa física
          (PF, ex.: profissional autônomo).
        </p>
        <div
          className="emp-doc-toggle emp-doc-toggle-lg"
          role="tablist"
          aria-label="Tipo de cadastro do workspace"
        >
          <button
            type="button"
            role="tab"
            aria-selected={isPj}
            className={`emp-doc-tab ${isPj ? "is-on" : ""}`}
            onClick={() => setDocType("cnpj")}
          >
            <Icon name="building" size={13} /> CNPJ{" "}
            <span className="emp-doc-tab-tag">Pessoa Jurídica</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={!isPj}
            className={`emp-doc-tab ${!isPj ? "is-on" : ""}`}
            onClick={() => setDocType("cpf")}
          >
            <Icon name="user" size={13} /> CPF{" "}
            <span className="emp-doc-tab-tag">Pessoa Física</span>
          </button>
        </div>
      </div>

      <div className="onb-section">
        <div className="onb-section-title">Dados do workspace</div>
        <div className="onb-field-grid onb-grid-2">
          <Field label="Nome do workspace" required>
            <input
              className="onb-input"
              placeholder={
                isPj ? "Ex: BRF Internal" : "Ex: Escritório João Silva"
              }
              maxLength={80}
              value={form.nomeWorkspace}
              onChange={(e) => setField("nomeWorkspace", e.target.value)}
            />
          </Field>
          {isPj ? (
            <Field
              label="CNPJ da empresa"
              required
              hint={
                looking
                  ? "consultando Receita Federal..."
                  : "preenchemos os campos abaixo automaticamente"
              }
            >
              <div className="onb-input-wrap">
                <Icon name="building" size={14} />
                <input
                  className="onb-input bare"
                  placeholder="00.000.000/0001-00"
                  inputMode="numeric"
                  maxLength={18}
                  value={form.cnpj}
                  onChange={(e) => setField("cnpj", formatCnpj(e.target.value))}
                  onBlur={() => { void lookup(); }}
                />
              </div>
            </Field>
          ) : (
            <Field
              label="CPF"
              required
              hint={
                form.cpf && !isCpfLen(form.cpf)
                  ? "CPF incompleto"
                  : "pessoa física — preenchimento manual"
              }
            >
              <div className="onb-input-wrap">
                <Icon name="user" size={14} />
                <input
                  className="onb-input bare"
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  maxLength={14}
                  value={form.cpf}
                  onChange={(e) => setField("cpf", formatCpf(e.target.value))}
                />
              </div>
            </Field>
          )}
        </div>
      </div>

      <div className="onb-section">
        <div className="onb-section-title">
          {isPj ? "Dados cadastrais (auto-preenchidos)" : "Dados cadastrais"}
        </div>
        {isPj ? (
          <div className="onb-field-grid onb-grid-2">
            <Field label="Razão social" required>
              <input
                className="onb-input"
                maxLength={200}
                value={form.razao}
                onChange={(e) => setField("razao", e.target.value)}
                placeholder="—"
              />
            </Field>
            <Field label="Nome fantasia">
              <input
                className="onb-input"
                maxLength={100}
                value={form.fantasia}
                onChange={(e) => setField("fantasia", e.target.value)}
                placeholder="—"
              />
            </Field>
          </div>
        ) : (
          <Field label="Nome completo" required>
            <input
              className="onb-input"
              maxLength={120}
              value={form.nomePf}
              onChange={(e) => setField("nomePf", e.target.value)}
              placeholder="Nome conforme CPF"
            />
          </Field>
        )}
        <div className="onb-field-grid onb-grid-3-1">
          <Field label="Logradouro">
            <input
              className="onb-input"
              maxLength={255}
              value={form.endereco}
              onChange={(e) => setField("endereco", e.target.value)}
              placeholder="Rua / Av."
            />
          </Field>
          <Field label="Número">
            <input
              className="onb-input"
              maxLength={20}
              value={form.numero}
              onChange={(e) => setField("numero", e.target.value)}
              placeholder="—"
            />
          </Field>
        </div>
        <div className="onb-field-grid onb-grid-2">
          <Field label="Complemento">
            <input
              className="onb-input"
              maxLength={100}
              value={form.complemento}
              onChange={(e) => setField("complemento", e.target.value)}
              placeholder="—"
            />
          </Field>
          <Field label="Bairro">
            <input
              className="onb-input"
              maxLength={120}
              value={form.bairro}
              onChange={(e) => setField("bairro", e.target.value)}
              placeholder="—"
            />
          </Field>
        </div>
        <div className="onb-field-grid onb-grid-3-1">
          <Field label="Cidade">
            <input
              className="onb-input"
              maxLength={120}
              value={form.cidade}
              onChange={(e) => setField("cidade", e.target.value)}
              placeholder="—"
            />
          </Field>
          <Field label="UF">
            <select
              className="onb-input"
              value={form.uf}
              onChange={(e) => setField("uf", e.target.value)}
            >
              <option value="">—</option>
              {UFS.map((uf) => (
                <option key={uf}>{uf}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="onb-field-grid onb-grid-2">
          <Field label="CEP">
            <input
              className="onb-input"
              maxLength={9}
              value={form.cep}
              onChange={(e) => setField("cep", e.target.value)}
              placeholder="00000-000"
            />
          </Field>
        </div>
        <div className="onb-field-grid onb-grid-2">
          <Field label="Telefone">
            <input
              className="onb-input"
              inputMode="tel"
              maxLength={15}
              value={form.telefone}
              onChange={(e) =>
                setField("telefone", fmtTelefone(e.target.value))
              }
              placeholder="(11) 0000-0000"
            />
          </Field>
          <Field label="E-mail de contato">
            <input
              className="onb-input"
              type="email"
              maxLength={120}
              value={form.emailEmpresa}
              onChange={(e) => setField("emailEmpresa", e.target.value)}
              placeholder={
                isPj ? "contato@empresa.com.br" : "voce@email.com.br"
              }
            />
          </Field>
        </div>
      </div>

      {isPj && (
        <div className="onb-section">
          <div className="onb-section-title">Responsável legal</div>
          <p className="onb-section-sub">
            Pessoa que pode tomar decisões pelo CNPJ — geralmente sócio ou
            administrador.
          </p>
          <div className="onb-field-grid onb-grid-2">
            <Field label="Nome completo" required>
              <input
                className="onb-input"
                maxLength={100}
                value={form.respNome}
                onChange={(e) => setField("respNome", e.target.value)}
                placeholder="João da Silva"
              />
            </Field>
            <Field
              label="CPF"
              required
              hint={
                form.respCpf && !isCpfLen(form.respCpf)
                  ? "CPF incompleto"
                  : undefined
              }
            >
              <input
                className="onb-input"
                inputMode="numeric"
                maxLength={14}
                value={form.respCpf}
                onChange={(e) => setField("respCpf", formatCpf(e.target.value))}
                placeholder="000.000.000-00"
              />
            </Field>
          </div>
          <Field label="Cargo / função" required>
            <input
              className="onb-input"
              maxLength={100}
              value={form.respCargo}
              onChange={(e) => setField("respCargo", e.target.value)}
              placeholder="Sócio-administrador"
            />
          </Field>
        </div>
      )}

      <div className="onb-section">
        <div className="onb-section-title">Usuário administrador (owner)</div>
        <p className="onb-section-sub">
          Quem vai gerenciar o workspace no TDAX. Você pode convidar outros
          membros depois.
        </p>
        <Field label="Nome do administrador" required>
          <input
            className="onb-input"
            maxLength={100}
            value={form.ownerNome}
            onChange={(e) => setField("ownerNome", e.target.value)}
            placeholder="João da Silva"
          />
        </Field>

        <WsVerifyField
          label="E-mail"
          channel="email"
          required
          placeholder="joao@empresa.com.br"
          type="email"
          icon="mail"
          inputMode="email"
          maxLength={120}
          value={form.ownerEmail}
          onChangeValue={(v) => {
            setField("ownerEmail", v);
            if (form.emailVerificado) setField("emailVerificado", false);
          }}
          isVerified={form.emailVerificado}
          onVerified={() => setField("emailVerificado", true)}
          hint="enviaremos um código de 6 dígitos · usado como login no TDAX"
        />

        <WsVerifyField
          label="Celular (WhatsApp/SMS)"
          channel="sms"
          required
          placeholder="(11) 90000-0000"
          type="tel"
          icon="phone"
          inputMode="tel"
          maxLength={15}
          formatter={fmtTelefone}
          value={form.ownerTelefone}
          onChangeValue={(v) => {
            setField("ownerTelefone", v);
            if (form.telefoneVerificado) setField("telefoneVerificado", false);
          }}
          isVerified={form.telefoneVerificado}
          onVerified={() => setField("telefoneVerificado", true)}
          hint="enviaremos um SMS com código de 6 dígitos · usado para 2FA"
        />

        <div className="onb-field-grid onb-grid-2">
          <Field
            label="Senha"
            required
            hint={
              form.ownerSenha && form.ownerSenha.length < 8
                ? "mínimo 8 caracteres"
                : "mínimo 8 caracteres"
            }
          >
            <input
              className="onb-input"
              type="password"
              minLength={8}
              maxLength={64}
              value={form.ownerSenha}
              onChange={(e) => setField("ownerSenha", e.target.value)}
              placeholder="••••••••"
            />
          </Field>
          <Field
            label="Confirmar senha"
            required
            hint={
              form.ownerSenha &&
              form.ownerSenha2 &&
              form.ownerSenha !== form.ownerSenha2
                ? "as senhas não conferem"
                : undefined
            }
          >
            <input
              className="onb-input"
              type="password"
              maxLength={64}
              value={form.ownerSenha2}
              onChange={(e) => setField("ownerSenha2", e.target.value)}
              placeholder="••••••••"
            />
          </Field>
        </div>
      </div>

      <div className="onb-section onb-section-terms">
        <Checkbox
          checked={form.aceiteTermos}
          onChange={(v) => setField("aceiteTermos", v)}
        >
          Li e aceito os{" "}
          <a href="#" className="onb-link">
            Termos de uso
          </a>{" "}
          e a{" "}
          <a href="#" className="onb-link">
            Política de privacidade
          </a>{" "}
          do TDAX.
        </Checkbox>
        <Checkbox
          checked={form.aceiteLgpd}
          onChange={(v) => setField("aceiteLgpd", v)}
        >
          Autorizo o Tributo Devido a armazenar e processar os dados informados
          conforme a{" "}
          <a href="#" className="onb-link">
            LGPD (Lei 13.709/2018)
          </a>
          .
        </Checkbox>
      </div>
    </>
  );
}
